import { Database, Users, DollarSign, Star, AlertTriangle } from "lucide-react";
import { money } from "@/lib/api";

export const feet = (m) => { const ft = m * 3.28084; return ft < 5280 ? `${(Math.round(ft / 10) * 10).toLocaleString()} ft` : `${(ft / 5280).toFixed(1)} mi`; };

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4">
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1"><Icon className="h-3.5 w-3.5" />{label}</p>
    <p data-testid={testId} className="num text-xl font-bold">{value ?? "—"}</p>
  </div>
);

const PlaceTable = ({ title, rows, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</p>
    <div className="rounded-lg border border-slate-800 max-h-72 overflow-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} className="border-t border-slate-800/70 first:border-0">
              <td className="px-3 py-2"><p className="text-slate-200">{p.name}</p><p className="text-xs text-slate-500">{p.type}</p></td>
              <td className="px-3 py-2 num text-xs text-slate-400 whitespace-nowrap">{feet(p.distance_m)}</td>
              <td className="px-3 py-2 num text-xs whitespace-nowrap">{p.rating ? <span className="inline-flex items-center gap-1 text-amber-300"><Star className="h-3 w-3 fill-current" />{p.rating} <span className="text-slate-500">({p.reviews})</span></span> : "—"}</td>
            </tr>
          ))}
          {!rows.length && <tr><td className="px-3 py-4 text-slate-500 text-xs">None found</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

export const LiveSiteData = ({ data }) => {
  if (!data) return null;
  const demo = data.demographics || {};
  return (
    <div className="mb-8 rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-5 space-y-5 print-avoid" data-testid="live-site-data">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-cyan-300"><Database className="h-4 w-4" />Live location data</p>
        <p className="text-xs text-slate-500">{data.sources?.length ? `Sources: ${data.sources.join(", ")}` : "No live sources available"}</p>
      </div>
      {data.formatted_address && <p className="text-xs text-slate-400">Verified address: {data.formatted_address}{demo.tract_name ? ` · ${demo.tract_name}` : ""}</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Tract population" value={demo.population?.toLocaleString()} testId="live-population" />
        <Stat icon={DollarSign} label="Median HH income" value={demo.median_household_income ? money(demo.median_household_income) : null} testId="live-income" />
        <Stat icon={Users} label="Median age" value={demo.median_age} testId="live-age" />
        <Stat icon={Star} label="Competitors found" value={data.competitors?.length ?? null} testId="live-competitor-count" />
      </div>
      {demo.note && <p className="text-xs text-amber-300/80">{demo.note}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 [&>*]:min-w-0">
        <PlaceTable title="Similar businesses (Google Places)" rows={data.competitors || []} testId="live-competitors" />
        <PlaceTable title="Within ½ mile (2,625 ft): anchors & traffic drivers" rows={data.nearby || []} testId="live-nearby" />
      </div>
      {data.errors?.length > 0 && <p className="flex items-start gap-2 text-xs text-amber-300/80"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{data.errors.join(" · ")}</p>}
    </div>
  );
};
